import { AxiosInstance } from "axios";
import ApiClient from "./ApiClient";
import {
  OrganizationDTO,
  OrganizationCreateDTO,
  OrganizationUpdateDTO,
} from "../types/Organization";

// Base URL for the Organizations API
const API_URL = process.env.REACT_APP_MENTORING_API_URL || "";

/**
 * OrganizationService provides methods to interact with the Organization API endpoints.
 * All methods return Promises that resolve to the response data or throw an error.
 */
class OrganizationService {
  private apiClient: AxiosInstance;

  constructor() {
    this.apiClient = new ApiClient(API_URL).getClient();
  }

  /**
   * List all organizations for the current user
   * @returns Promise resolving to an array of OrganizationDTO
   */
  public listOrganizations = async (): Promise<OrganizationDTO[]> => {
    const response = await this.apiClient.get<OrganizationDTO[]>("/organizations");
    return response.data;
  };

  /**
   * Create a new organization
   * @param organizationData The organization data to create
   * @returns Promise resolving to the created OrganizationDTO
   */
  public createOrganization = async (
    organizationData: OrganizationCreateDTO
  ): Promise<OrganizationDTO> => {
    const response = await this.apiClient.post<OrganizationDTO>(
      "/organizations",
      organizationData
    );
    return response.data;
  };

  /**
   * Get details of a specific organization
   * @param organizationId The ID of the organization to retrieve
   * @returns Promise resolving to the OrganizationDTO
   */
  public getOrganization = async (
    organizationId: string
  ): Promise<OrganizationDTO> => {
    const response = await this.apiClient.get<OrganizationDTO>(
      `/organizations/${organizationId}`
    );
    return response.data;
  };

  /**
   * Update an existing organization
   * @param organizationId The ID of the organization to update
   * @param updates The fields to update
   * @returns Promise resolving to the updated OrganizationDTO
   */
  public updateOrganization = async (
    organizationId: string,
    updates: OrganizationUpdateDTO
  ): Promise<OrganizationDTO> => {
    const response = await this.apiClient.patch<OrganizationDTO>(
      `/organizations/${organizationId}`,
      updates
    );
    return response.data;
  };

  /**
   * Delete an organization
   * @param organizationId The ID of the organization to delete
   * @returns Promise that resolves when the organization is deleted
   */
  public deleteOrganization = async (organizationId: string): Promise<void> => {
    await this.apiClient.delete(`/organizations/${organizationId}`);
  };
}

const organizationService = new OrganizationService();
export default organizationService;